import React, { useRef } from "react";
import clsx from "clsx";
import { ScrollToFormikError } from "../elements/ScrollToFormikError";
import { motion } from "framer-motion";


interface ITextAreaProps {
    label?: string;
    id?: string;
    description?: string;
    disabled?: boolean;
    readOnly?: boolean;
    name: string;
    placeholder?: string;
    value: string | number;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void | null;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement, Element>) => void | null;
    error?: string | undefined;
    required?: boolean;
    helperText?: string;
    autoComplete?: boolean;
    classes?: string;
    resize?: "vertical" | "horizontal" | "none" | "both" | "block" | "inline" | undefined;
    maxHeight?: number | undefined;
    minHeight?: number | undefined;
    maxWidth?: number | undefined;
    minWidth?: number | undefined;
    rows?: number;
    ref?: any;
    max?: number;
    labelClasses?: string;
}

function TextArea({
    label,
    id,
    description,
    disabled = false,
    name,
    placeholder,
    value,
    readOnly = false,
    onChange,
    onBlur,
    error,
    required = false,
    helperText,
    autoComplete = false,
    resize,
    maxHeight,
    minHeight = 50,
    maxWidth,
    minWidth,
    classes,
    rows,
    ref,
    max,
    labelClasses
}: ITextAreaProps): JSX.Element {
    const fieldRef = useRef<HTMLDivElement>(null);

    return (
        <div className={clsx("w-full", { ["animate-wiggle"]: error && required })}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={id}
                    className={clsx(
                        "block text-sm font-medium text-gray-700 dark:text-darkBg-mainText",
                        { ["!text-danger-main"]: error && required },
                        labelClasses
                    )}
                >
                    {label} <span className="!text-danger-main">{required && "*"}</span>
                    {description && (<p className={clsx("text-xs text-gray-400 dark:text-darkBg-subText italic", { ["!text-danger-border"]: error && required })}>{description}</p>)}
                </label>
            )}

            <div className="mt-1 relative">
                <div className="relative w-full" ref={fieldRef}>
                    <textarea
                        name={name}
                        id={id}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={clsx(
                            "relative w-full border border-gray-300 dark:border-darkBg-border rounded-md shadow-sm p-2 text-left cursor-default",
                            "focus:outline-none focus:ring-1 focus:ring-primary-main focus:border-primary-border sm:text-sm",
                            "flex items-center placeholder:text-gray-400 dark:placeholder:text-darkBg-subText",
                            classes,
                            disabled ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed" : "bg-white dark:bg-darkBg dark:text-white",
                            { ["bg-red-200 !border-danger-main focus:ring-danger-main focus:border-danger-border"]: error && required },
                            readOnly && 'border-none',
                            { ['border-red-200 focus:ring-red-200 focus:border-red-200']: !value && required }
                        )}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        disabled={disabled}
                        value={value}
                        autoComplete={String(autoComplete)}
                        style={{
                            resize: resize,
                            maxHeight: maxHeight,
                            minHeight: minHeight,
                            maxWidth: maxWidth,
                            minWidth: minWidth
                        }}
                        rows={rows}
                        ref={ref}
                        maxLength={max}
                    />
                </div>
            </div>

            {/* Helper Text */}
            <div className={clsx("text-xs text-gray-400 dark:text-darkBg-subText mt-1", { ["!text-danger-main"]: error })}> {helperText && (
                <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-600 flex items-center font-medium"
                >
                    <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {helperText && typeof helperText === 'string' ? helperText : undefined}
                </motion.p>
            )}</div>
            <ScrollToFormikError
                fieldName={name ?? id ?? ""}
                fieldRef={fieldRef}
            />
        </div>
    )
}

export default TextArea;